import { roomEquipmentPositions } from "./C1_roomEquipmentLayout";
import type { C1_Room } from "./C1_Room";
import { evalExpression } from "../lib/C1_evalExpression";
import type C1_EloItem from "./C1_EloItem";

export type C1_EloFormulaError = {
  itemId: string;
  definitionId: string;
  axis: "x" | "y";
  expression: string;
  message: string;
};

/** Evaluates definition formulas and transforms one ELO hierarchy into plan coordinates. */
export class C1_EloSpatialEvaluator {
  evaluatedDefinitionPosition?: { x: number; y: number };
  evaluatedPosition?: { x: number; y: number };
  evaluatedAbsolutePosition?: { x: number; y: number };
  /** Formula result before room-boundary corrections, used to rank display collisions. */
  formulaAbsolutePosition?: { x: number; y: number };

  constructor(private readonly item: C1_EloItem) {}

  /** Evaluates local positions first and then applies the owner's global transformation. */
  evaluateAbsolutePlacements(
    origin: { x: number; y: number },
    rotationInDegree = 0,
    reflect = false,
    props: Record<string, number> = {},
    onFormulaError?: (error: C1_EloFormulaError) => void,
  ): void {
    this.evalRelativePlacements(props, { x: 0, y: 0 }, undefined, onFormulaError);
    this.calcAbsolutePositions(origin.x, origin.y, rotationInDegree, reflect);
    const rememberFormulaPosition = (evaluation: C1_EloSpatialEvaluator) => {
      evaluation.formulaAbsolutePosition = evaluation.evaluatedAbsolutePosition
        ? { ...evaluation.evaluatedAbsolutePosition } : undefined;
      for (const child of evaluation.item.spatialChildren) rememberFormulaPosition(child.spatialEvaluation);
    };
    rememberFormulaPosition(this);
    if (this.item.definition?.definitionId === "room") {
      const room = this.item.owner?.c1 as C1_Room | undefined;
      if (room?.className === "C1_Room") {
        const automatic = this.item.spatialChildren.filter(child =>
          child._defId.startsWith(`${this.item._defId}/`) && !child.interactivCreated && !child.position && child.evaluatedAbsolutePosition);
        const positions = roomEquipmentPositions(automatic.map(child => child.evaluatedAbsolutePosition!), room.placementGeometry(), origin);
        const translate = (item: C1_EloItem, dx: number, dy: number) => {
          const spatial = item.spatialEvaluation;
          if (spatial.evaluatedAbsolutePosition) spatial.evaluatedAbsolutePosition = {
            x: spatial.evaluatedAbsolutePosition.x + dx, y: spatial.evaluatedAbsolutePosition.y + dy};
          if (spatial.evaluatedPosition) spatial.evaluatedPosition = {
            x: spatial.evaluatedPosition.x + dx, y: spatial.evaluatedPosition.y + dy};
          for (const child of item.spatialChildren) translate(child, dx, dy);
        };
        automatic.forEach((child, index) => {
          const previous = child.evaluatedAbsolutePosition!;
          translate(child, positions[index].x - previous.x, positions[index].y - previous.y);
        });
      }
    }
  }

  /** Applies reflection, rotation and translation to the evaluated hierarchy. */
  calcAbsolutePositions(originX: number, originY: number, rotationInDegree: number, reflect = false): void {
    const independentPosition = this.item.independentAbsolutePosition;
    if (independentPosition) {
      this.evaluatedAbsolutePosition = independentPosition;
      const rotation = this.item.data.interactivePlacement?.rotation ?? 0;
      const radians = rotation * Math.PI / 180;
      const local = this.evaluatedPosition ?? { x: 0, y: 0 };
      const childOriginX = independentPosition.x - local.x * Math.cos(radians) + local.y * Math.sin(radians);
      const childOriginY = independentPosition.y - local.x * Math.sin(radians) - local.y * Math.cos(radians);
      for (const child of this.item.spatialChildren) {
        child.spatialEvaluation.calcAbsolutePositions(childOriginX, childOriginY, rotation);
      }
      return;
    }
    const radians = (rotationInDegree * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const reflectFactor = reflect ? -1 : 1;

    if (this.evaluatedPosition) {
      const x = this.evaluatedPosition.x * reflectFactor;
      const y = this.evaluatedPosition.y;
      this.evaluatedAbsolutePosition = {
        x: originX + x * cos - y * sin,
        y: originY + x * sin + y * cos,
      };
    }

    for (const child of this.item.spatialChildren) {
      child.spatialEvaluation.calcAbsolutePositions(originX, originY, rotationInDegree, reflect);
    }
  }

  /** Evaluates local definition formulas and accumulates parent positions recursively. */
  private evalRelativePlacements(
    props: Record<string, number>,
    parentPosition: { x: number; y: number },
    parentRepeatIndex: number | undefined,
    onFormulaError: ((error: C1_EloFormulaError) => void) | undefined,
    siblingIndex?: number,
    siblingCount?: number,
  ): void {
    const definition = this.item.definition;
    const repeatIndex = this.item.data.repeatIndex === undefined ? parentRepeatIndex : this.item.data.repeatIndex - 1;
    const signX = Math.sign(parentPosition.x);
    const signY = Math.sign(parentPosition.y);
    const context: Record<string, number> = {
      ...props,
      SignX: signX,
      SignY: signY,
      SignGX: signX,
      SignGY: signY,
      ...(repeatIndex === undefined ? {} : { I: repeatIndex }),
      ...(siblingIndex === undefined ? {} : { ItemIndex: siblingIndex }),
      ...(siblingCount === undefined ? {} : { ItemCount: siblingCount }),
    };
    const x = this.evaluateAxis(definition?.position?.x || 0, "x", context, onFormulaError);
    const y = this.evaluateAxis(definition?.position?.y || 0, "y", context, onFormulaError);

    this.evaluatedDefinitionPosition = { x, y };
    this.evaluatedPosition = this.item.position
      ? { ...this.item.position }
      : { x: parentPosition.x + x, y: parentPosition.y + y };

    const children = this.item.spatialChildren;
    // Furniture and independently placed devices are not members of the room equipment layout.
    const layoutChildren = this.item.definition?.definitionId === "room"
      ? children.filter(child => child._defId.startsWith(`${this.item._defId}/`) && !child.interactivCreated)
      : children;
    for (let index = 0; index < children.length; index += 1) {
      const layoutIndex = layoutChildren.indexOf(children[index]);
      children[index].spatialEvaluation.evalRelativePlacements(
        props,
        this.evaluatedPosition,
        repeatIndex,
        onFormulaError,
        layoutIndex >= 0 ? layoutIndex : index,
        layoutIndex >= 0 ? layoutChildren.length : children.length,
      );
    }
  }

  /** Evaluates one numeric axis and reports invalid catalog expressions. */
  private evaluateAxis(
    value: number | string,
    axis: "x" | "y",
    context: Record<string, number>,
    onFormulaError: ((error: C1_EloFormulaError) => void) | undefined,
  ): number {
    if (typeof value !== "string") return value;
    try {
      return evalExpression(value, context);
    } catch (error) {
      onFormulaError?.({
        itemId: this.item.owner?.id ?? "",
        definitionId: this.item.definitionId,
        axis,
        expression: value,
        message: error instanceof Error ? error.message : String(error),
      });
      return 0;
    }
  }
}
